import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal, Stagger, StaggerItem } from "../motion/space-primitives";
import { listArticles } from "../../lib/articles";

const LATEST_COUNT = 3;

const ArticleCard = ({ post }) => {
  const accent = post.theme?.accent || "#99DFAF";
  return (
    <Link
      to={`/articles/${post.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] no-underline transition-all hover:-translate-y-1 hover:border-primary/30"
    >
      <div className="relative flex h-40 items-center justify-center overflow-hidden">
        {post.coverImageUrl ? (
          <img
            src={post.coverImageUrl}
            alt={post.title}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0" style={{ background: post.theme?.coverGradient }} />
        )}
        {post.theme?.logo && (
          <img
            src={post.theme.logo}
            alt={`${post.title} logo`}
            className="relative h-12 w-auto opacity-90 transition-opacity group-hover:opacity-100"
          />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-6">
        <span
          className="w-fit rounded-full px-3 py-1 text-xs font-semibold"
          style={{ color: accent, backgroundColor: `${accent}1A` }}
        >
          {post.tags?.[0] || "Update"}
        </span>
        <h3 className="text-lg font-bold leading-snug text-white transition-colors group-hover:text-primary">
          {post.title}
        </h3>
        <p className="flex-1 text-sm text-gray-400">{post.excerpt}</p>
        <span className="text-xs text-gray-500">{post.date}</span>
      </div>
    </Link>
  );
};

// Latest-articles teaser for the main landing page. Silently renders nothing
// until there's at least one published article, so it's safe to always mount.
const ArticlesPreview = () => {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    listArticles()
      .then((all) => setArticles(all.slice(0, LATEST_COUNT)))
      .catch(() => setArticles([]));
  }, []);

  if (articles.length === 0) return null;

  return (
    <section className="relative w-full bg-space px-8 py-24 max-sm:px-6 lg:px-20">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 className="text-4xl font-bold text-white max-sm:text-3xl">
            Latest <span className="text-brand-gradient italic">articles</span>
          </h2>
          <Link
            to="/articles"
            className="text-sm font-semibold text-primary no-underline hover:underline"
          >
            View all articles →
          </Link>
        </div>
      </Reveal>
      <Stagger className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-3">
        {articles.map((post) => (
          <StaggerItem key={post.slug}>
            <ArticleCard post={post} />
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
};

export default ArticlesPreview;
